import React, { useEffect, useRef, useState } from "react";
import { all_routes } from "../../../Router/all_routes";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorToaster } from "../../components/toaster/Toaster";
import InputField from "../../components/Input";
import UserServices from "../../../services/User";
import { PlusCircle, X } from "feather-icons-react/build/IconComponents";
import { emailRegex, passwordRegex, encryptText } from "../../../utils";
import { FormControlLabel, Switch } from "@mui/material";
import { IMAGE_BASE_URL } from "../../../config/constants";

export const Form = ({ onSubmit, loading }) => {

  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams();
  const imageRef = useRef();

  const [pageLoading, setPageLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm();
  const password = useRef({});
  password.current = watch("password", "");

  // *For Password Visibility Toggle
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  // *For Token Visibility Toggle
  const [isSandboxTokenVisible, setIsSandboxTokenVisible] = useState(false);
  const [isLiveTokenVisible, setIsLiveTokenVisible] = useState(false);

  // *For Status
  const [status, setStatus] = useState(true);

  // *For Logo
  const [isLogo, setIsLogo] = useState({});

  // *For Upload Image
  const allowFilesType = ['image/png', 'image/jpg', 'image/jpeg']
  const handleUploadImage = (e) => {
    try {
      if (e.target.value !== "") {
        const file = e.target.files[0]

        if (file.size > 1024 * 1024) { // 1 MB limit
          ErrorToaster("File size must be less than 1MB");
          return;
        }

        if (allowFilesType.includes(file.type)) {
          const imgReader = new FileReader();
          imgReader.onload = (event) => {
            let obj = {
              file: file,
              image: event.target.result
            }
            setIsLogo(obj)
          };
          imgReader.readAsDataURL(file);
        } else {
          ErrorToaster("Only png, jpg, and jpeg formats is supported")
        }
      }
    } catch (error) {
      console.log('handleUploadImage => error:', error)
    }
  };

  const handleRemoveImage = () => {
    try {
      setIsLogo({})
      imageRef.current.value = null;
    } catch (error) {
      console.log('handleRemoveImage => error:', error)
    }
  };

  // *For Create/Update User
  const submitForm = async (data) => {
    try {
      const formData = new FormData();
      formData.append('name', data?.name);
      formData.append('phone', data?.phone);
      formData.append('email', data?.email);
      formData.append('ntn_cnic', data?.ntnCnic);
      formData.append('address', data?.address);
      formData.append('province', data?.province);
      formData.append('status', status ? 'Active' : 'Inactive');
      if (isLogo?.file) {
        formData.append('logo', isLogo?.file);
      }
      if (data?.password) {
        formData.append('password', data?.password);
      }
      if (data?.sandboxToken) {
        formData.append('sandbox_token', encryptText(data?.sandboxToken));
      }
      if (data?.liveToken) {
        formData.append('live_token', encryptText(data?.liveToken));
      }
      onSubmit(formData)
    } catch (error) {
      console.log('🚀 => submitForm => error:', error)
    }
  };

  // *For Get User Details
  const getUserDetail = async () => {
    setPageLoading(true)
    try {
      const { data } = await UserServices.getUserById(id)
      setValue('name', data?.name)
      setValue('phone', data?.phone)
      setValue('email', data?.email)
      setValue('ntnCnic', data?.ntn_cnic)
      setValue('address', data?.address)
      setValue('province', data?.province)
      setStatus(data?.status === 'Active' ? true : false)
      setIsLogo({
        file: null,
        image: `${IMAGE_BASE_URL}${data?.logo}`
      })
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setPageLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      getUserDetail()
    }
  }, [id]);

  return (
    <>
      {pageLoading ? (
        <div id="global-loader" className="page-loader">
          <div className="whirly-loader"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(submitForm)}>
          <div className="card">
            <div className="card-body add-product pb-0" style={{ padding: '1rem' }}>
              <div className="row">
                <div className="col-lg-3 col-sm-6 col-12">
                  <label className="form-label">Company Logo</label>
                  <div className="add-choosen">
                    {isLogo?.image ? (
                      <div className="phone-img mb-3" style={{ marginLeft: '0px' }}>
                        <img
                          ref={imageRef}
                          src={isLogo?.image}
                          alt="image"
                          style={{
                            height: "120px",
                            width: "120px"
                          }}
                        />
                        <Link to="#">
                          <X
                            className="x-square-add remove-product"
                            onClick={() => handleRemoveImage()}
                          />
                        </Link>
                      </div>
                    ) : (
                      <div className="input-blocks mb-3">
                        <div className="image-upload mb-2" style={{ height: '123px', width: '137px' }}>
                          <input type="file" onChange={(e) => handleUploadImage(e)} />
                          <div className="image-uploads" style={{ padding: '43px 0px' }}>
                            <PlusCircle className="plus-down-add me-0" />
                            <h4>Add Logo</h4>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Name*"
                      type="text"
                      error={errors?.name?.message}
                      register={register("name", {
                        required: 'Please enter user name.',
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Phone"
                      type="tel"
                      register={register("phone")}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Email*"
                      type="email"
                      error={errors?.email?.message}
                      register={register("email", {
                        required: 'Please enter billing email.',
                        pattern: {
                          value: emailRegex,
                          message: 'Please enter a valid email.',
                        }
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="NTN/CNIC*"
                      type="text"
                      error={errors?.ntnCnic?.message}
                      register={register("ntnCnic", {
                        required: 'Please enter NTN/CNIC.',
                        minLength: { value: 7, message: 'NTN/CNIC must be at least 7 digits.' },
                        maxLength: { value: 7, message: 'NTN/CNIC must be at most 7 digits.' },
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Address*"
                      type="text"
                      error={errors?.address?.message}
                      register={register("address", {
                        required: 'Please enter address.',
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Province*"
                      type="text"
                      error={errors?.province?.message}
                      register={register("province", {
                        required: 'Please enter province.',
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Password"
                      type={isPasswordVisible ? "text" : "password"}
                      endAdornment={
                        <span
                          className={`fas toggle-password ${isPasswordVisible ? "fa-eye" : "fa-eye-slash"
                            }`}
                          onClick={() => setPasswordVisible((prevState) => !prevState)}
                        ></span>
                      }
                      error={errors?.password?.message}
                      register={register("password", {
                        required: !id && 'Please enter the password.',
                        pattern: {
                          value: passwordRegex,
                          message: 'Password contain minimum 8 characters, at least uppercase/lowercase letter, number and special character',
                        }
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Confirm Password"
                      type={isConfirmPasswordVisible ? "text" : "password"}
                      endAdornment={
                        <span
                          className={`fas toggle-password ${isConfirmPasswordVisible ? "fa-eye" : "fa-eye-slash"
                            }`}
                          onClick={() => setConfirmPasswordVisible((prevState) => !prevState)}
                        ></span>
                      }
                      error={errors?.confirmPassword?.message}
                      register={register("confirmPassword", {
                        required: !id && 'Please enter the confirm password.',
                        validate: value => value === password.current || "Confirm password does not match"
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Sandbox Token"
                      type={isSandboxTokenVisible ? "text" : "password"}
                      endAdornment={
                        <span
                          className={`fas toggle-password ${isSandboxTokenVisible ? "fa-eye" : "fa-eye-slash"
                            }`}
                          onClick={() => setIsSandboxTokenVisible((prevState) => !prevState)}
                        ></span>
                      }
                      register={register("sandboxToken")}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6 col-12">
                  <div className="mb-2 add-product">
                    <InputField
                      label="Live Token"
                      type={isLiveTokenVisible ? "text" : "password"}
                      endAdornment={
                        <span
                          className={`fas toggle-password ${isLiveTokenVisible ? "fa-eye" : "fa-eye-slash"
                            }`}
                          onClick={() => setIsLiveTokenVisible((prevState) => !prevState)}
                        ></span>
                      }
                      register={register("liveToken")}
                    />
                  </div>
                </div>
                <div className="col-lg-12">
                  <FormControlLabel
                    labelPlacement="start"
                    label="Status"
                    sx={{ ml: 0 }}
                    control={
                      <Switch checked={status} onChange={(e) => setStatus(e.target.checked)} />
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="btn-addproduct mb-4">
              <button type="button" className="btn btn-cancel me-2" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-submit"
                disabled={loading}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
};